import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';

const selectors = {
  modalClose: '[data-close-modal]',
  modalClosePress: '[data-close-modal-mousedown]',
  modalPanel: '[data-modal-panel]',
};

const mobileModalQuery = window.matchMedia('(max-width: 767px)');

function isMobileModal(modal) {
  return mobileModalQuery.matches && modal.hasAttribute('data-modal');
}

function getModalPanel(modal) {
  return modal.querySelector(selectors.modalPanel);
}

function finishCloseModal(modal, modalId) {
  modal.close();
  enablePageScroll();

  modal.dispatchEvent(
    new CustomEvent('modal:closed', {
      detail: { id: modalId },
    }),
  );
}

function animateMobilePanelIn(panel) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      panel.style.transform = 'translateY(0)';
    });
  });
}

// Matches the 0.2s transform transition on [data-modal-panel] in
// src/styles/base/dialog.css, with headroom for a busy main thread.
const MOBILE_PANEL_TRANSITION_MS = 400;

function animateMobilePanelOut(modal, panel, modalId) {
  // transitionend is the happy path, but it never fires if the panel is
  // already at translateY(100%) (an open that raced a backgrounded tab, so
  // the rAF that slides it in never ran) or if the transition is interrupted.
  // Without a fallback the dialog stays open with page scroll still locked —
  // a modal the shopper genuinely cannot close.
  let settled = false;
  let fallbackTimer = null;

  const settle = () => {
    if (settled) {
      return;
    }

    settled = true;
    clearTimeout(fallbackTimer);
    panel.removeEventListener('transitionend', handleTransitionEnd);
    finishCloseModal(modal, modalId);
  };

  const handleTransitionEnd = event => {
    if (event.target !== panel || event.propertyName !== 'transform') {
      return;
    }

    settle();
  };

  fallbackTimer = setTimeout(settle, MOBILE_PANEL_TRANSITION_MS);

  panel.addEventListener('transitionend', handleTransitionEnd);
  panel.style.transform = 'translateY(100%)';
}

/**
 * Opens a modal by its ID
 * @param {string} modalId - The ID of the modal to open
 * @returns {boolean} - Returns true if modal was opened successfully, false if modal not found
 *
 * Emits:
 *   'modal:opened' (CustomEvent) on the modal element when opened. Event detail contains { id: modalId }
 */
export function openModal(modalId) {
  const modal = document.getElementById(modalId);

  if (!modal) {
    console.warn(`Modal with ID "${modalId}" not found`);
    return false;
  }

  const panel = getModalPanel(modal);

  if (panel) {
    if (isMobileModal(modal)) {
      panel.style.transform = 'translateY(100%)';
    } else {
      panel.style.removeProperty('transform');
    }
  }

  modal.showModal();
  disablePageScroll();

  if (panel && isMobileModal(modal)) {
    animateMobilePanelIn(panel);
  }

  // Emit custom event when modal is opened
  modal.dispatchEvent(
    new CustomEvent('modal:opened', {
      detail: { id: modalId },
    }),
  );

  const handleCloseModalPress = event => {
    event.preventDefault();
    handleCloseModal();
  };

  // Helper to close modal and cleanup listeners
  const handleCloseModal = () => {
    closeModal(modalId);
    modal.removeEventListener('click', backdropHandler);
    modal.querySelectorAll(selectors.modalClose).forEach(modalClose => {
      modalClose.removeEventListener('click', handleCloseModal);
    });
    modal.querySelectorAll(selectors.modalClosePress).forEach(modalClose => {
      modalClose.removeEventListener('pointerdown', handleCloseModalPress);
    });
  };

  // Backdrop handler needs to be referenced for removal
  const backdropHandler = backdropEvent => {
    if (backdropEvent.target === modal) {
      handleCloseModal();
    }
  };

  // Attach listeners
  modal.addEventListener('click', backdropHandler);
  modal.querySelectorAll(selectors.modalClose).forEach(modalClose => {
    modalClose.addEventListener('click', handleCloseModal);
  });
  modal.querySelectorAll(selectors.modalClosePress).forEach(modalClose => {
    modalClose.addEventListener('pointerdown', handleCloseModalPress);
  });

  return true;
}

/**
 * Closes a modal by its ID
 * @param {string} modalId - The ID of the modal to close
 * @returns {boolean} - Returns true if modal was closed successfully, false if modal not found
 *
 * Emits:
 *   'modal:closed' (CustomEvent) on the modal element when closed. Event detail contains { id: modalId }
 */
export function closeModal(modalId) {
  const modal = document.getElementById(modalId);

  if (!modal) {
    console.warn(`Modal with ID "${modalId}" not found`);
    return false;
  }

  const panel = getModalPanel(modal);

  if (panel && isMobileModal(modal) && modal.open) {
    animateMobilePanelOut(modal, panel, modalId);
    return true;
  }

  finishCloseModal(modal, modalId);

  return true;
}
